import { supabase } from '../main.js';
import { icon } from '../utils/icons.js';
import { footerElement } from '../components/Footer.js'
import { openLightbox } from '../components/Lightbox.js';

function safeUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(url, location.origin);
    return (u.protocol === 'https:' || u.protocol === 'http:') ? url : '';
  } catch { return ''; }
}

function makeIcon(name, opts) {
  const span = document.createElement('span');
  span.className = 'btn-icon';
  span.appendChild(document.createRange().createContextualFragment(icon(name, opts)));
  return span;
}

// Turns an image tile into a button that opens the lightbox at its position
function makeZoomable(el, viewerImages, image) {
  const index = viewerImages.push(image) - 1;
  el.type = 'button';
  el.classList.add('cs-zoomable');
  el.setAttribute('aria-label', 'View larger: ' + image.alt);
  const hint = document.createElement('span');
  hint.className = 'cs-zoom-hint';
  hint.appendChild(document.createRange().createContextualFragment(icon('maximize', { size: 16 })));
  el.appendChild(hint);
  el.addEventListener('click', () => openLightbox(viewerImages, index));
}

export async function renderProjectDetailPage(container, slug) {
  const loading = document.createElement('section');
  loading.className = 'rm-page';
  loading.innerHTML = '<div class="rm-container"><div class="rm-loading">Loading case study…</div></div>';
  container.replaceChildren(loading);

  try {
    let project = null;

    if (supabase) {
      const timeout = new Promise(res => setTimeout(() => res({ data: null, error: 'timeout' }), 4000));
      const query = supabase.from('portfolio_projects').select('*').eq('slug', slug).single();
      const { data, error } = await Promise.race([query, timeout]);
      if (!error && data) project = data;
    }

    if (!project) project = getMockProject(slug);

    if (project) {
      renderCaseStudy(container, project);
    } else {
      const notFound = document.createElement('section');
      notFound.className = 'rm-page';
      notFound.innerHTML = [
        '<div class="rm-container">',
        '<h1 class="rm-page-title">Project Not Found</h1>',
        '<p>The case study you\'re looking for doesn\'t exist.</p>',
        '<a href="/projects" class="rm-btn rm-btn-primary">Back to Work</a>',
        '</div>'
      ].join('');
      container.replaceChildren(notFound);
    }
  } catch (err) {
    console.error('Error loading project detail:', err);
    const errEl = document.createElement('section');
    errEl.className = 'rm-page';
    errEl.innerHTML = [
      '<div class="rm-container">',
      '<h1 class="rm-page-title">Error Loading Project</h1>',
      '<a href="/projects" class="rm-btn rm-btn-primary">Back to Work</a>',
      '</div>'
    ].join('');
    container.replaceChildren(errEl);
  }
}

function getMockProject(slug) {
  const projects = {
    'plush-club': {
      slug: 'plush-club',
      title: 'Plush Club',
      category: 'Game / Mobile',
      year: '2026',
      timeline: 'Ongoing',
      status: 'Demo live',
      description: 'A cozy 3D claw machine game for iPhone and the web. Catch plush friends, tear open foil prize packs and fill an album of collectible cards.',
      problem: 'Most mobile claw games decide the outcome with a hidden dice roll, so a catch feels random and a miss feels rigged. The goal was a claw where aim, grip pressure and timing decide whether a toy holds, rolls or slips, wrapped in a collection loop worth coming back to.',
      solution: 'One Expo and React Native codebase runs the game on iPhone and in the browser, with the cabinet rendered through React Three Fiber and Three.js. Physics runs at 120 Hz in Rapier\'s deterministic WebAssembly build: three hinged claw fingers driven by torque-limited motors hold a toy by friction alone, with no hidden attachment, so an uneven pinch can rotate, slip or drop it. Every plush was modeled and rigged in Blender with stuffing that squashes under the fingers, then baked down to a small streamed mesh per species. A catch drops into a foil pouch you tear open by dragging across the seal, revealing a tilting 3D card with satin and holographic editions. A Cloudflare Worker with D1 serves the site and the API for players, purchases through RevenueCat and Sign in with Apple.',
      outcome: 'The free web demo is live at plushclub.app with a waitlist for the iPhone launch, and the App Store listing and in-app play packs are set up. The album holds 67 friends across seasonal series, and a test suite covers the grip physics, from friction limits and finger stalls to every plush shape being catchable.',
      image_url: '/images/plushclub-cover.jpg',
      gallery: ['/images/plushclub-1.jpg', '/images/plushclub-2.jpg', '/images/plushclub-3.jpg', '/images/plushclub-4.jpg', '/images/plushclub-5.jpg', '/images/plushclub-6.jpg'],
      technologies: ['Expo', 'React Native', 'TypeScript', 'React Three Fiber', 'Three.js', 'Rapier', 'Blender', 'Cloudflare Workers', 'D1', 'RevenueCat'],
      demo_url: 'https://plushclub.app',
      github_url: null
    },
    'halo-ce': {
      slug: 'halo-ce',
      title: 'Halo CE',
      category: 'Game / Multiplayer',
      year: '2026',
      timeline: 'Ongoing',
      status: 'Playable',
      description: 'A browser multiplayer recreation of Halo: Combat Evolved. Sixteen-player matches on Blood Gulch and Guardian, playable from a link with no download.',
      problem: 'Classic Halo multiplayer meant shared hardware, an install, and everyone owning the game. The goal was a link you send a friend that drops them straight into Blood Gulch, with the feel of the 2001 original and 60fps on a mid-tier laptop.',
      solution: 'Three.js renders the maps, and each match runs as its own Cloudflare Durable Object acting as the authoritative server. The netcode is Source-style: a 60 Hz server tick, 20 Hz binary snapshots, client prediction with reconciliation, and a 200 ms rewind window so hitscan shots land where you aimed. Player movement is a hand-rolled swept capsule rather than a physics engine, which keeps the client and server simulations identical. Nine weapons, several modeled in Blender from reference, plus a drivable Warthog, Ghost and Banshee. Phones get thumb controls automatically. Every visual piece was judged blind against reference frames from the original game.',
      outcome: 'Live and playable in the browser. Each match holds 16 players, any number of matches run at once, and sharing the URL brings a friend into the same game. Because Cloudflare doesn\'t bill outbound WebSocket messages, broadcasting world state to a full lobby costs nothing, and the backend runs on the Workers free plan. Blood Gulch and Guardian are playable now, with Lockout in progress.',
      image_url: '/images/halo-ce-cover.jpg',
      gallery: ['/images/halo-ce-guardian.jpg'],
      technologies: ['Three.js', 'TypeScript', 'Cloudflare Durable Objects', 'WebSockets', 'Blender', 'Vite'],
      demo_url: 'https://nothalo.lol',
      github_url: null
    },
    'hoa-right': {
      slug: 'hoa-right',
      title: 'HOA Right',
      category: 'SaaS / Mobile',
      year: '2026',
      timeline: '5 months to launch',
      status: 'Live',
      description: 'Modular community management for HOAs. Boards turn on only the tools they need, and every staff role sees exactly what it should.',
      problem: 'HOA boards and staff ran their communities on spreadsheets, group texts and paper logs. Work orders got lost in email, nobody knew whether a package had been picked up, and the management software on offer bundled dozens of features a small community would never use while giving the gatehouse the same access as the board.',
      solution: 'Built one Expo and React Native codebase that ships to the web, iOS and Android. Communities toggle modules on and off: homeowner directory, work orders, equipment, expenses, package tracking, announcements and messaging. Access is split across Admin, Front Desk, Gatehouse, Maintenance and Homeowner roles, enforced in Postgres with Supabase row-level security instead of in the UI. Photos live in Cloudflare R2 behind a Worker that asks the database which images the signed-in user may see. A shared calendar puts maintenance jobs, equipment bookings, reservations and community events in one view.',
      outcome: 'Launched on July 23, 2026, with eight releases since. Those added crew scheduling and reporting, clubhouse and equipment reservations, double-booking warnings, spreadsheet import, member export and mailing labels at any size. A public changelog tells boards and residents what\'s new in plain language.',
      image_url: '/images/hoaright-cover.jpg',
      gallery: [],
      technologies: ['Expo', 'React Native', 'TypeScript', 'Supabase', 'Cloudflare R2', 'Tamagui'],
      demo_url: 'https://hoaright.com',
      github_url: null
    },
    'notchdeck': {
      slug: 'notchdeck',
      title: 'NotchDeck',
      category: 'macOS App',
      year: '2026',
      timeline: 'Ongoing',
      status: 'Released',
      description: 'A native macOS app that turns the MacBook notch into a drawer of notes, tasks and a pomodoro timer.',
      problem: 'Jotting a note, checking a to-do or starting a focus timer usually means switching to another app and losing your place. Meanwhile the notch on a MacBook is dead space at the top of the screen that nothing uses.',
      solution: 'Built a small native app in Swift and SwiftUI, with no Electron. NotchDeck sits as a black sliver that blends into the hardware notch and opens into a deck of widgets when the pointer moves in: a notes scratchpad, a task list with colored folders and drag-to-reorder, and a pomodoro timer with a progress ring. While it\'s closed, the open task count and the running countdown show beside the camera. It never takes keyboard focus from the app you\'re working in, works across Spaces and over full-screen apps, and docks to the left or right screen edge on Macs without a notch. Notes and tasks are stored locally, with no account.',
      outcome: 'Released as a universal build for Apple silicon and Intel, signed with a Developer ID certificate and notarized by Apple, so it opens like any other Mac app. Pushing a version tag builds, signs, notarizes and publishes a GitHub release, and Sparkle delivers each update in place. Free and open source under the MIT license.',
      image_url: '/images/notchdeck-cover.jpg',
      gallery: ['/images/notchdeck-notes.jpg', '/images/notchdeck-pomodoro.jpg'],
      technologies: ['Swift', 'SwiftUI', 'Sparkle', 'GitHub Actions'],
      demo_url: 'https://github.com/cadenburleson/notchdeck/releases/latest',
      github_url: 'https://github.com/cadenburleson/notchdeck'
    },
    'called-or-not': {
      slug: 'called-or-not',
      title: 'Called or Not',
      category: 'SaaS / Automation',
      year: '2026',
      timeline: 'Ongoing',
      status: 'Launching',
      description: 'An automated phone assistant that calls your drug-testing hotline every morning, keys in your code, listens to the announcement and texts you the answer.',
      problem: 'People on court-ordered testing programs have to call a color hotline every day to find out whether they need to test, and one missed call can mean a violation. The one existing automated service charges $14 a month.',
      solution: 'Built entirely on Cloudflare: Astro on Workers for the site and dashboard, D1 for data, R2 for call recordings, and Cron Triggers for scheduling. Every morning a dispatcher places a Twilio call, enters the PIN with DTMF tones and records the announcement. Whisper transcribes the recording, Claude classifies it, and the user gets a text: no test today, test day, or a "check manually" alert if the hotline couldn\'t be reached after retries. PINs are encrypted at rest, sign-in uses Google or email magic links, and Stripe handles subscriptions. An admin console manages hotlines, plays back recordings for review and can dial a line on demand.',
      outcome: 'The full call engine, auth, billing, dashboard and admin are built and deployed, covered by 28 unit tests and a 37-step end-to-end integration test. Pricing is set at $6.99 a month, half the incumbent\'s price, with a 30% recurring affiliate program so users can refer each other.',
      image_url: '/images/calledornot-cover.jpg',
      gallery: [],
      technologies: ['Astro', 'Cloudflare Workers', 'D1', 'R2', 'Twilio', 'Claude', 'Stripe', 'TypeScript'],
      demo_url: 'https://calledornot.com',
      github_url: null
    },
    'shotscraft': {
      slug: 'shotscraft',
      title: 'ShotsCraft',
      category: 'Open Source / Tool',
      year: '2026',
      timeline: 'Ongoing',
      status: 'Live',
      description: 'A free, open-source studio for App Store screenshots and marketing videos that runs entirely in the browser, with nothing to install.',
      problem: 'App Store screenshots and launch videos usually mean a paid design tool for the stills, a separate video editor for the reels, and redoing all of it for every language the app ships in.',
      solution: 'Forked YUZU-Hub\'s MIT-licensed appscreen generator and turned it into a full marketing studio. Added video playback with native video and GIF export including audio, a keyframe animation system on a unified timeline, marketing-video reels and a template library. Built a MacBook model alongside the iPhone and iPad, groups and layers, copy and paste across slides, and a Three.js render pass with depth of field, glass screens and studio lighting. Everything runs client-side, with projects saved to IndexedDB.',
      outcome: 'Live and free at shotscraft.com. It exports every App Store size plus 10 social and video presets, in every language at once, with optional AI translation using your own API key.',
      image_url: '/images/shotscraft-cover.jpg',
      gallery: [],
      technologies: ['JavaScript', 'Three.js', 'ffmpeg.wasm', 'IndexedDB', 'Tauri'],
      demo_url: 'https://shotscraft.com',
      github_url: 'https://github.com/cadenburleson/shotscraft'
    },
    'bricked-up': {
      slug: 'bricked-up',
      title: 'BrickedUp',
      category: 'Marketplace',
      year: '2025',
      timeline: '10 weeks',
      status: 'Live',
      description: 'The construction jobs marketplace built for the trades — no agency fees, verified portfolios, and a credit system that rewards reliable workers.',
      problem: 'Construction workers and contractors had no reliable direct-hire platform. Staffing agencies charged fees to both sides, portfolios were unverifiable, and there was no accountability system to separate serious tradespeople from no-shows.',
      solution: 'Built a two-sided marketplace where laborers create verified portfolio profiles and contractors post jobs directly. A reputation-based credit system rewards on-time work and quality reviews with better visibility and lower platform fees over time. Stripe handles payouts with built-in escrow so workers get paid on completion.',
      outcome: 'Launched to the trades community with zero agency fees on either side. Early contractors reported filling roles 60% faster than through traditional staffing. The credit system drove a 78% job completion rate in the first cohort.',
      image_url: '/images/bricked-up-cover.jpg',
      gallery: [],
      technologies: ['React', 'Node.js', 'Supabase', 'Stripe'],
      demo_url: 'https://bricked-up.com',
      github_url: null
    },
    'byheadless': {
      slug: 'byheadless',
      title: 'Byheadless',
      category: 'SaaS / CMS',
      year: '2025',
      timeline: '6 weeks',
      status: 'Live',
      description: 'A headless CMS that gives clients full content control through a simple interface — integrated into any project with a single line of code.',
      problem: 'Every client project ended the same way: the client needed to update content, but had no way to do it without calling a developer. Building a custom CMS for each project was expensive, and off-the-shelf solutions like Contentful or Sanity required days of setup, complex API configuration, and a learning curve most clients couldn\'t handle.',
      solution: 'Built Byheadless — a lightweight headless CMS that developers drop into any project with a single line of code. Clients get a clean, intuitive dashboard to create and manage pages and blog posts without ever touching the codebase. The API is self-documenting and returns structured JSON, so no custom integration work is needed on the frontend. Built the entire auth, editor, and delivery layer from scratch.',
      outcome: 'Reduced client onboarding time from 2 days to under an hour. Clients can publish new content independently without developer involvement. The single-line integration means it ships as part of every new client project by default.',
      image_url: '/images/byheadless-cover.jpg',
      gallery: [],
      technologies: ['React', 'Node.js', 'Supabase', 'REST API'],
      demo_url: 'https://byheadless.com',
      github_url: null
    },
    'dungindragin': {
      slug: 'dungindragin',
      title: 'DunginDragin',
      category: 'AI / Game',
      year: '2024',
      timeline: '8 weeks',
      status: 'Live',
      description: 'AI-powered D&D platform with infinite procedurally generated worlds and a dynamic AI Dungeon Master.',
      problem: 'Tabletop RPG enthusiasts wanted rich, personalized D&D experiences without needing to gather a group or wait for a human DM. The barrier to entry — finding players, scheduling sessions, learning complex rules — was keeping millions of potential players away from a genre they would love.',
      solution: 'Built an AI-powered platform using Google Gemini to act as an infinite Dungeon Master — generating procedural worlds, dynamic narratives, and adaptive story beats in real time. Designed a clean, atmospheric UI that evokes classic D&D without feeling dated, with character creation, biome exploration, and an item marketplace.',
      outcome: 'Launched publicly with zero downtime. Users average 45-minute sessions with a 61% Day-7 retention rate. The procedural engine generates unique adventures across 12+ distinct biomes, making every playthrough feel fresh.',
      image_url: 'https://www.dungindragin.com/images/og-image.png',
      gallery: [],
      technologies: ['Google Gemini AI', 'React', 'Node.js', 'MongoDB'],
      demo_url: 'https://www.dungindragin.com',
      github_url: null
    },
    'fitnitapp': {
      slug: 'fitnitapp',
      title: 'FitNit App',
      category: 'Mobile / Health',
      year: '2024',
      timeline: '12 weeks',
      status: 'Live',
      description: 'A friction-free fitness and nutrition tracking app that keeps users engaged past the first week.',
      problem: 'Existing fitness apps required too much manual input — logging every meal, manually adding exercises, searching through databases. The friction was killing retention. Most users abandoned their app within 7 days of downloading it.',
      solution: 'Redesigned the logging experience around speed: photo-based food recognition via OpenAI, a barcode scanner, and smart workout suggestions based on past history. Built in React Native for a native feel across iOS and Android, with a progress dashboard that visualizes gains in a way that actually motivates.',
      outcome: '4.8-star App Store rating within 3 months of launch. 68% Day-30 retention rate — 3x the industry average. Average session time of 4 minutes, down from 12 minutes with competing apps.',
      image_url: 'https://fitnitapp.com/assets/feature-image.jpg',
      gallery: [],
      technologies: ['React Native', 'Node.js', 'PostgreSQL', 'OpenAI'],
      demo_url: 'https://fitnitapp.com',
      github_url: null
    },
    'hard-ship': {
      slug: 'hard-ship',
      title: 'Hard-Ship',
      category: 'Web Application',
      year: '2024',
      timeline: '10 weeks',
      status: 'Live',
      description: 'A platform for endurance athletes to plan expeditions, track performance, and compete in challenges.',
      problem: 'Adventure athletes had no unified platform to plan multi-day expeditions, track performance metrics, and connect with a community that matched their intensity. Existing tools were either too generic or too narrow.',
      solution: 'Built a full-stack platform with real-time GPS expedition tracking via Mapbox, a challenge system with daily and monthly leaderboards, and team-based group challenges. Supabase handles real-time data sync so friends and family can track progress live.',
      outcome: 'Launched to a waitlist of 800+ users. 92% of users completed their first challenge within 7 days of joining. Featured in two endurance-focused newsletters within the first month.',
      image_url: '/images/hard-ship-cover.jpg',
      gallery: [],
      technologies: ['Next.js', 'Supabase', 'Tailwind CSS', 'Mapbox', 'TypeScript'],
      demo_url: 'https://hard-ship.com',
      github_url: null
    }
  };
  return projects[slug] || null;
}

function normalizeProject(project) {
  let technologies = [];
  if (Array.isArray(project.technologies)) {
    technologies = project.technologies;
  } else if (typeof project.technologies === 'string') {
    try { technologies = JSON.parse(project.technologies); }
    catch { technologies = project.technologies.split(',').map(t => t.trim()); }
  }
  let gallery = [];
  if (Array.isArray(project.gallery)) gallery = project.gallery;
  else if (Array.isArray(project.gallery_images)) gallery = project.gallery_images;
  return { ...project, technologies, gallery };
}

function renderCaseStudy(container, rawProject) {
  const project = normalizeProject(rawProject);

  // ── Hero ─────────────────────────────────────────────────────────────────
  const hero = document.createElement('section');
  hero.className = 'cs-detail-hero';
  const heroInner = document.createElement('div');
  heroInner.className = 'rm-container';

  const backLink = document.createElement('a');
  backLink.href = '/projects';
  backLink.className = 'rm-back-link cs-back';
  backLink.appendChild(makeIcon('arrow-left', { size: 14 }));
  backLink.appendChild(document.createTextNode(' Back to Work'));

  const metaRow = document.createElement('div');
  metaRow.className = 'cs-detail-meta';
  const catPill = document.createElement('span');
  catPill.className = 'cs-detail-category';
  catPill.textContent = project.category;
  const yearPill = document.createElement('span');
  yearPill.className = 'cs-detail-year';
  yearPill.textContent = project.year;
  metaRow.append(catPill, yearPill);

  const titleEl = document.createElement('h1');
  titleEl.className = 'cs-detail-title';
  titleEl.textContent = project.title;

  const descEl = document.createElement('p');
  descEl.className = 'cs-detail-desc';
  descEl.textContent = project.description;

  heroInner.append(backLink, metaRow, titleEl, descEl);
  hero.appendChild(heroInner);

  // ── Cover image ──────────────────────────────────────────────────────────
  const coverSection = document.createElement('section');
  coverSection.className = 'cs-detail-cover';
  const coverInner = document.createElement('div');
  coverInner.className = 'rm-container';
  // Every image opens in the lightbox, in page order: cover, then gallery
  const viewerImages = [];
  const imgUrl = safeUrl(project.image_url);
  const coverImg = document.createElement(imgUrl ? 'button' : 'div');
  coverImg.className = 'cs-detail-cover-img';
  if (imgUrl) {
    coverImg.style.cssText = 'background-size:cover;background-position:center top;';
    coverImg.style.backgroundImage = 'url(' + imgUrl + ')';
    makeZoomable(coverImg, viewerImages, { src: imgUrl, alt: project.title + ' cover image' });
  }
  coverInner.appendChild(coverImg);
  coverSection.appendChild(coverInner);

  // ── Stat strip ───────────────────────────────────────────────────────────
  const statsSection = document.createElement('section');
  statsSection.className = 'cs-detail-stats';
  const statsInner = document.createElement('div');
  statsInner.className = 'rm-container';
  const statsGrid = document.createElement('div');
  statsGrid.className = 'cs-stats-grid';

  [
    { label: 'Timeline', value: project.timeline || 'Custom' },
    { label: 'Type',     value: project.category },
    { label: 'Status',   value: project.status || 'Live' }
  ].forEach(({ label, value }) => {
    const stat = document.createElement('div');
    stat.className = 'cs-stat';
    const lbl = document.createElement('p');
    lbl.className = 'cs-stat-label';
    lbl.textContent = label;
    const val = document.createElement('p');
    val.className = 'cs-stat-value';
    val.textContent = value;
    stat.append(lbl, val);
    statsGrid.appendChild(stat);
  });
  statsInner.appendChild(statsGrid);
  statsSection.appendChild(statsInner);

  // ── Body: main + sidebar ─────────────────────────────────────────────────
  const bodySection = document.createElement('section');
  bodySection.className = 'cs-detail-body';
  const bodyInner = document.createElement('div');
  bodyInner.className = 'rm-container';
  const bodyGrid = document.createElement('div');
  bodyGrid.className = 'cs-body-grid';

  const mainCol = document.createElement('div');
  mainCol.className = 'cs-body-main';

  [
    { label: 'The Challenge', text: project.problem,   key: 'challenge' },
    { label: 'The Solution',  text: project.solution,  key: 'solution'  },
    { label: 'The Outcome',   text: project.outcome,   key: 'outcome'   }
  ].forEach(({ label, text, key }) => {
    if (!text) return;
    const block = document.createElement('div');
    block.className = 'cs-body-block cs-body-block--' + key;
    const h2 = document.createElement('h2');
    h2.className = 'cs-body-heading';
    h2.textContent = label;
    const p = document.createElement('p');
    p.className = 'cs-body-text';
    p.textContent = text;
    block.append(h2, p);
    mainCol.appendChild(block);
  });

  const sidebar = document.createElement('aside');
  sidebar.className = 'cs-body-sidebar';

  if (project.technologies && project.technologies.length > 0) {
    const techBlock = document.createElement('div');
    techBlock.className = 'cs-sidebar-block';
    const techLbl = document.createElement('p');
    techLbl.className = 'cs-sidebar-label';
    techLbl.textContent = 'Stack';
    const techPills = document.createElement('div');
    techPills.className = 'cs-sidebar-tech';
    project.technologies.forEach(t => {
      const pill = document.createElement('span');
      pill.className = 'rm-tech-tag';
      pill.textContent = t;
      techPills.appendChild(pill);
    });
    techBlock.append(techLbl, techPills);
    sidebar.appendChild(techBlock);
  }

  if (project.demo_url || project.github_url) {
    const linkBlock = document.createElement('div');
    linkBlock.className = 'cs-sidebar-block';
    const linkLbl = document.createElement('p');
    linkLbl.className = 'cs-sidebar-label';
    linkLbl.textContent = 'Links';
    linkBlock.appendChild(linkLbl);

    if (project.demo_url) {
      const a = document.createElement('a');
      a.href = safeUrl(project.demo_url);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.className = 'rm-btn rm-btn-primary cs-sidebar-btn';
      a.textContent = 'View Live ';
      a.appendChild(makeIcon('arrow-up-right', { size: 14 }));
      linkBlock.appendChild(a);
    }

    if (project.github_url) {
      const a = document.createElement('a');
      a.href = safeUrl(project.github_url);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.className = 'rm-btn rm-btn-secondary cs-sidebar-btn';
      a.textContent = 'GitHub';
      linkBlock.appendChild(a);
    }

    sidebar.appendChild(linkBlock);
  }

  bodyGrid.append(mainCol, sidebar);
  bodyInner.appendChild(bodyGrid);
  bodySection.appendChild(bodyInner);

  // ── Gallery ──────────────────────────────────────────────────────────────
  const galSection = document.createElement('section');
  galSection.className = 'cs-detail-gallery';
  if (project.gallery && project.gallery.length > 0) {
    const galInner = document.createElement('div');
    galInner.className = 'rm-container';
    const galGrid = document.createElement('div');
    galGrid.className = 'cs-gallery-grid';
    let shotNumber = 0;
    project.gallery.forEach(src => {
      const url = safeUrl(src);
      if (!url) return;
      const alt = project.title + ' screenshot ' + (++shotNumber);
      const item = document.createElement('button');
      item.className = 'cs-gallery-item';
      // Shown at its own aspect ratio; phone screens get a narrower column
      const img = document.createElement('img');
      img.src = url;
      img.alt = alt;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.addEventListener('load', () => {
        if (img.naturalHeight > img.naturalWidth) item.classList.add('cs-gallery-item--tall');
      });
      item.appendChild(img);
      makeZoomable(item, viewerImages, { src: url, alt });
      galGrid.appendChild(item);
    });
    galInner.appendChild(galGrid);
    galSection.appendChild(galInner);
  }

  // ── Bottom CTA ───────────────────────────────────────────────────────────
  const ctaSection = document.createElement('section');
  ctaSection.className = 'rm-bento';
  const ctaInner = document.createElement('div');
  ctaInner.className = 'rm-container';
  const ctaCard = document.createElement('div');
  ctaCard.className = 'bento-card bento-12 bento-card--dark bento-final-cta';

  const ctaEyebrow = document.createElement('p');
  ctaEyebrow.className = 'rm-eyebrow';
  ctaEyebrow.textContent = 'Start a Project';

  const ctaHeading = document.createElement('h2');
  ctaHeading.textContent = 'Want results like these?';

  const ctaBody = document.createElement('p');
  ctaBody.textContent = 'Let\'s build something together. One flat rate, no contracts.';

  const ctaRow = document.createElement('div');
  ctaRow.className = 'cta-row';

  const ctaBtn = document.createElement('a');
  ctaBtn.href = 'https://cal.com/cadenburleson';
  ctaBtn.target = '_blank';
  ctaBtn.rel = 'noopener noreferrer';
  ctaBtn.className = 'rm-btn rm-btn-primary';
  ctaBtn.textContent = 'Get Started ';
  ctaBtn.appendChild(makeIcon('arrow-right', { size: 16 }));

  const workBtn = document.createElement('a');
  workBtn.href = '/projects';
  workBtn.className = 'rm-btn rm-btn-secondary';
  workBtn.textContent = 'More Work';

  ctaRow.append(ctaBtn, workBtn);
  ctaCard.append(ctaEyebrow, ctaHeading, ctaBody, ctaRow);
  ctaInner.appendChild(ctaCard);
  ctaSection.appendChild(ctaInner);

  // ── Footer ───────────────────────────────────────────────────────────────
  const footer = footerElement();

  container.replaceChildren(hero, coverSection, statsSection, bodySection, galSection, ctaSection, footer);
}
